import axios from 'axios'
import config from '../config/env'
import type {
  SignUpDto,
  LoginDto,
  LoginResponse,
  MemberCreateDto,
  MemberUpdateDto,
  MemberSacramentCreateDto,
  MemberSacramentUpdateDto,
  ApprovalDto,
  PasswordChangeDto,
  LockAccountDto,
  RelationShipCreateDto,
} from './models'
import type { Router } from 'vue-router'
import type { AuthStore } from '../stores/auth'
import { useAuthStore } from '../stores/auth'
import { setupAuthInterceptors } from '../utils/authInterceptor'

let router: Router | null = null

const apiClient = axios.create({
  baseURL: config.api.baseUrl,
  timeout: config.api.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
})

let authInterceptorsSetup = false

export function initializeApi(_router: Router, getAuthStore: () => AuthStore) {
  router = _router
  if (!authInterceptorsSetup) {
    setupAuthInterceptors(apiClient, getAuthStore, router)
    authInterceptorsSetup = true
  }
}

// Add request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    const token = authStore.token

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Update response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    if (error.response?.status === 401) {
      const authStore = useAuthStore()
      authStore.logout()
      if (router) {
        await router.push({ name: 'login' })
      }
    }
    return Promise.reject(error)
  },
)

export const authApi = {
  signup: async (signUpDto: SignUpDto) => {
    return await apiClient.post('/Auth/signup', signUpDto)
  },
  login: async (loginDto: LoginDto) => {
    return await apiClient.post<LoginResponse>('/Auth/login', loginDto)
  },
  adminChangePassword: async (passwordChangeDto: PasswordChangeDto) => {
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) {
      throw new Error('Not authenticated')
    }
    return await apiClient.post('/Auth/admin/changepassword', passwordChangeDto)
  },
}

export const membersApi = {
  createMember: async (memberCreateDto: MemberCreateDto) => {
    return apiClient.post('/Members/member', memberCreateDto)
  },
  getMemberById: async (id: string) => {
    return apiClient.get(`/Members/member/${id}`)
  },
  updateMemberById: async (id: string, memberUpdateDto: MemberUpdateDto) => {
    if (!useAuthStore().isAuthenticated) {
      throw new Error('Not authenticated')
    }
    return apiClient.put(`/Members/member/${id}`, memberUpdateDto)
  },
  deleteMemberById: async (id: string) => {
    const authStore = useAuthStore()

    if (!authStore.isAuthenticated) {
      throw new Error('Not authenticated')
    }

    return apiClient.delete(`/Members/member/${id}`)
  },
  getMembers: async (params: {
    FilterIsActive?: boolean
    FilterIsParent?: boolean
    SearchEmail?: string
    SearchMobileNo?: string
    SearchMemberNo?: number
    PageNo?: number
    Count?: number
  }) => {
    return apiClient.get('/Members/members', {
      params,
      // Add error handling for empty responses
      validateStatus: (status) => {
        return status >= 200 && status < 300
      },
    })
  },
  addMemberSacraments: async (id: string, memberSacramentCreateDtos: MemberSacramentCreateDto[]) => {
    return apiClient.post(`/Members/member/${id}/addSacraments`, memberSacramentCreateDtos)
  },
  updateMemberSacrament: async (memberId: string, id: string, memberSacramentUpdateDto: MemberSacramentUpdateDto) => {
    return apiClient.put(`/Members/member/${memberId}/sacrament/${id}`, memberSacramentUpdateDto)
  },
  approveMember: async (id: string, approvalDto: ApprovalDto) => {
    if (!useAuthStore().isAuthenticated) {
      throw new Error('Not authenticated')
    }
    return apiClient.post(`/Members/approve/${id}`, approvalDto)
  },
  deleteMemberSacrament: async (memberId: string, id: string) => {
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) {
      throw new Error('Not authenticated')
    }
    return apiClient.delete(`/Members/member/${memberId}/sacrament/${id}`)
  },
  lockAccount: async (id: string, lockAccountDto: LockAccountDto) => {
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) {
      throw new Error('Not authenticated')
    }
    return apiClient.post(`/Members/admin/lockaccount/${id}`, lockAccountDto)
  },
  createRelationship: async (relationShipCreateDto: RelationShipCreateDto) => {
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) {
      throw new Error('Not authenticated')
    }
    return apiClient.post('/Members/relationship', relationShipCreateDto)
  },
  getRelationshipsByMember: async (memberId: string) => {
    return apiClient.get(`/Members/relationship/${memberId}`)
  },
  deleteRelationship: async (id: string) => {
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) {
      throw new Error('Not authenticated')
    }
    return apiClient.delete(`/Members/relationship/${id}`)
  },
}

// Export types for external use
export type {
  LoginResponse,
  SignUpDto,
  LoginDto,
  MemberCreateDto,
  MemberUpdateDto,
  MemberSacramentCreateDto,
  MemberSacramentUpdateDto,
  ApprovalDto,
  PasswordChangeDto,
  LockAccountDto,
  RelationShipCreateDto,
  GenderEnum,
  ApprovalStatus,
  SacramentEnum,
  RelationShipType,
  TitleType,
} from './models'
